// ViewReceiptDialog - Display receipt file preview from Azure Storage
// Fetches SAS URL and displays images/PDFs directly

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { receiptsAPI } from '../../services/api';
import type { Receipt } from '../../types';
import dayjs from 'dayjs';

interface ViewReceiptDialogProps {
  open: boolean;
  receipt: Receipt;
  onClose: () => void;
}

const ViewReceiptDialog: React.FC<ViewReceiptDialogProps> = ({
  open,
  receipt,
  onClose,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch blob URL when dialog opens
  useEffect(() => {
    if (open && receipt.receiptId) {
      fetchBlobUrl();
    }

    return () => {
      // Clean up when dialog closes
      setBlobUrl(null);
      setError(null);
    };
  }, [open, receipt.receiptId]);

  const fetchBlobUrl = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Fetching blob URL for receipt:', receipt.receiptId);
      const response = await receiptsAPI.getViewUrl(
        receipt.submissionId,
        receipt.receiptId
      );
      setBlobUrl(response.url);
      console.log('✅ Got blob URL:', response.url);
    } catch (err: any) {
      console.error('❌ Error fetching blob URL:', err);
      const errorMsg = err.response?.data?.error || 'Failed to load receipt file';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  };

  // Determine file type - check both mimeType and file extension
  const fileExtension = receipt.originalFileName?.split('.').pop()?.toLowerCase() || '';
  
  // Check if it's an image
  const isImage = 
    receipt.mimeType?.startsWith('image/') || 
    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension);
  
  // Check if it's a PDF
  const isPdf = 
    receipt.mimeType === 'application/pdf' || 
    fileExtension === 'pdf';
  
  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('🔍 File type detection:', {
        mimeType: receipt.mimeType,
        fileExtension,
        isImage,
        isPdf,
        originalFileName: receipt.originalFileName
      });
    }
  }, [open, receipt, isImage, isPdf, fileExtension]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Receipt: {receipt.vendor}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Receipt Details */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Receipt Details
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
            <Typography variant="body2">
              <strong>Vendor:</strong> {receipt.vendor}
            </Typography>
            <Typography variant="body2">
              <strong>Amount:</strong> ${receipt.amount.toFixed(2)}
            </Typography>
            <Typography variant="body2">
              <strong>Category:</strong> {receipt.expenseCategory}
            </Typography>
            <Typography variant="body2">
              <strong>Date:</strong> {dayjs(receipt.receiptDate).format('MMM D, YYYY')}
            </Typography>
          </Box>
          {receipt.notes && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              <strong>Notes:</strong> {receipt.notes}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            File: {receipt.originalFileName || 'Unknown'}
          </Typography>
        </Box>

        {/* File Preview */}
        <Box
          sx={{
            minHeight: 450,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Loading State */}
          {loading && (
            <Box textAlign="center">
              <CircularProgress size={60} />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Loading receipt from Azure Storage...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {!loading && error && (
            <Box textAlign="center" p={4}>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
              <Button 
                variant="outlined" 
                onClick={fetchBlobUrl} 
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Box>
          )}

          {/* Success - Display File */}
          {!loading && !error && blobUrl && (
            <>
              {/* Image Preview */}
              {isImage && (
                <Box
                  component="img"
                  src={blobUrl}
                  alt={receipt.vendor}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                  onError={(e) => {
                    console.error('❌ Image failed to load:', blobUrl);
                    setError('Failed to load image. The file may be corrupted or the URL expired.');
                  }}
                />
              )}

              {/* PDF Preview */}
              {isPdf && (
                <Box
                  component="iframe"
                  src={blobUrl}
                  sx={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                    borderRadius: 1,
                  }}
                  title={`Receipt for ${receipt.vendor}`}
                  onError={(e) => {
                    console.error('❌ PDF failed to load:', blobUrl);
                    setError('Failed to load PDF. Try downloading instead.');
                  }}
                />
              )}

              {/* Other File Types - Download Only */}
              {!isImage && !isPdf && (
                <Box textAlign="center" p={4}>
                  <Typography variant="h6" gutterBottom>
                    Preview Not Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    This file type cannot be previewed in the browser.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    File type: {receipt.mimeType || fileExtension || 'Unknown'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!blobUrl || loading}
        >
          Download
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewReceiptDialog;